import {
	getMarketsAndOraclesForSubscription,
	initialize,
	Wallet,
} from '@velocity-exchange/sdk';
import {
	DELEGATE_SIGNING_MESSAGE_BUFFER_MS,
	EnvironmentConstants,
	Initialize as InitializeCommon,
	UIEnv,
	USER_SIGNING_MESSAGE_BUFFER_MS,
} from '@velocity-exchange/common';
import bs58 from 'bs58';
import { Commitment, Keypair } from '@solana/web3.js';
import { EnvironmentVariables, UserSettings } from './EnvironmentVariableTypes';
import { defaultUserSettings } from './DefaultSettings';
import { PriorityFee } from '../@types/types';
import { getNextDelistingTs, parseDelistingConfig } from 'src/utils/delisting';
import { chooseDefaultRpc, getRpcOptions } from 'src/utils/rpc';
import { E2eWalletAdapter } from 'src/utils/wallets/E2eWalletAdapter';
export * from './EnvironmentVariableTypes';
export * from './DefaultSettings';

const urlHasParam = (param: string) => {
	const urlSearchParams = new URLSearchParams(window.location.search);

	return urlSearchParams.has(param);
};

const getNumericEnvVar = (
	envVar: string | undefined,
	defaultValue: number
): number => {
	if (envVar === undefined || envVar === null) return defaultValue;
	const parsed = Number(envVar);
	return isNaN(parsed) ? defaultValue : parsed;
};

/** Parses a comma-separated list of market indexes (e.g. "1,27,66") into numbers. */
const parseMarketIndexList = (envVar: string | undefined): number[] => {
	if (!envVar) return [];
	return envVar
		.split(',')
		.map((entry) => Number(entry.trim()))
		.filter((n) => Number.isInteger(n) && n >= 0);
};

const isWindowDefined = () => typeof window !== 'undefined';

export const syncGetCurrentSettings = (): UserSettings | undefined => {
	if (!isWindowDefined()) return undefined;

	const settings = localStorage.getItem('settings');

	let parsedSettings = {};

	if (settings) {
		parsedSettings = JSON.parse(settings);
	}

	const result = {
		...defaultUserSettings,
		...parsedSettings,
	};

	// The devnet default itself lives in defaultUserSettings, so it is already
	// applied by the spread above.
	if (urlHasParam('useMainnet')) {
		result.devnetOverride = false;
	}

	return result;
};

// --- Environment Settings
const savedSettings = syncGetCurrentSettings();

// Default to mainnet-beta unless explicitly set to devnet via devnetOverride
const sdkEnv =
	savedSettings?.devnetOverride === true ? 'devnet' : 'mainnet-beta';

export const sdkConfig = initialize({ env: sdkEnv });
InitializeCommon(sdkEnv);

// default to weighted rpc selection to favor Helius without overloading one provider
const rpcToUse = chooseDefaultRpc(getRpcOptions(sdkEnv));

const dataApiEnvToUse: UIEnv =
	savedSettings?.stagingDataServerOverride ||
	process.env.NEXT_PUBLIC_FORCE_USE_STAGING_SERVER
		? UIEnv.createStaging()
		: sdkConfig.ENV === 'mainnet-beta'
			? UIEnv.createMainnet()
			: UIEnv.createDevnet();

// Segment used in dev proxy paths: /api/{data,dlob}-proxy/{segment}/*
const devProxyEnvSegment = dataApiEnvToUse.isStaging
	? ('staging' as const)
	: dataApiEnvToUse.isMainnet
		? ('mainnet' as const)
		: ('dev' as const);

// WebSocket connections bypass the Next.js proxy — rewrites don't support protocol upgrades.
const dataApiWsUrlToUse = process.env.NEXT_PUBLIC_DATA_API_URL_OVERRIDE
	? process.env.NEXT_PUBLIC_DATA_API_URL_OVERRIDE.replace(
			'https://',
			'wss://'
		).replace('http://', 'ws://') + '/ws'
	: (dataApiEnvToUse.isStaging
			? EnvironmentConstants.dataServerUrl.staging
			: dataApiEnvToUse.isMainnet
				? EnvironmentConstants.dataServerUrl.mainnet
				: EnvironmentConstants.dataServerUrl.dev
		).replace('https://', 'wss://') + '/ws';

const dataApiUrlToUse = process.env.NEXT_PUBLIC_DATA_API_URL_OVERRIDE
	? process.env.NEXT_PUBLIC_DATA_API_URL_OVERRIDE
	: dataApiEnvToUse.isStaging
		? EnvironmentConstants.dataServerUrl.staging
		: dataApiEnvToUse.isMainnet
			? EnvironmentConstants.dataServerUrl.mainnet
			: EnvironmentConstants.dataServerUrl.dev;

const dlobHttpServerToUse = process.env.NEXT_PUBLIC_DLOB_SERVER_OVERRIDE
	? process.env.NEXT_PUBLIC_DLOB_SERVER_OVERRIDE
	: savedSettings?.stagingDlobServerOverride
		? EnvironmentConstants.dlobServerHttpUrl.staging
		: sdkConfig.ENV === 'mainnet-beta'
			? EnvironmentConstants.dlobServerHttpUrl.mainnet
			: EnvironmentConstants.dlobServerHttpUrl.dev;

const dlobWsServerToUse = process.env.NEXT_PUBLIC_OVERRIDE_WS_URL
	? process.env.NEXT_PUBLIC_OVERRIDE_WS_URL
	: savedSettings?.stagingDlobServerOverride
		? EnvironmentConstants.dlobServerWsUrl.staging
		: sdkConfig.ENV === 'mainnet-beta'
			? EnvironmentConstants.dlobServerWsUrl.mainnet
			: EnvironmentConstants.dlobServerWsUrl.dev;

const eventsServerToUse = process.env.NEXT_PUBLIC_EVENTS_SERVER_OVERRIDE
	? process.env.NEXT_PUBLIC_EVENTS_SERVER_OVERRIDE
	: sdkConfig.ENV === 'mainnet-beta'
		? EnvironmentConstants.eventsServerUrl.mainnet
		: EnvironmentConstants.eventsServerUrl.dev;

// In local dev, proxy through Next.js to avoid CORS restrictions.
// The /api/swift-proxy/* rewrites in next.config.js forward to the real swift server server-side.
const swiftServerToUse = process.env.NEXT_PUBLIC_SWIFT_SERVER_URL_OVERRIDE
	? process.env.NEXT_PUBLIC_SWIFT_SERVER_URL_OVERRIDE
	: sdkConfig.ENV === 'mainnet-beta'
		? EnvironmentConstants.swiftServerUrl.mainnet
		: EnvironmentConstants.swiftServerUrl.dev;

const DEFAULT_WALLET = new Wallet(new Keypair());

// Accepts either a comma-separated byte array ("12,34,...") or a base58-encoded
// secret key (the format wallet apps export).
const parseE2eWalletSecret = (raw: string | undefined) => {
	if (!raw) return undefined;

	const trimmed = raw.trim().replace(/^\[|\]$/g, '');

	if (trimmed.includes(',')) {
		return Uint8Array.from(trimmed.split(',').map(Number));
	}

	return bs58.decode(trimmed);
};

const E2E_TEST_WALLET_SECRET = parseE2eWalletSecret(
	process.env.NEXT_PUBLIC_E2E_TEST_WALLET_SECRET
);
const getE2eWalletKeypair = () => {
	if (!E2E_TEST_WALLET_SECRET) return new Keypair();

	try {
		return Keypair.fromSecretKey(E2E_TEST_WALLET_SECRET);
	} catch (err) {
		console.warn(
			'Invalid NEXT_PUBLIC_E2E_TEST_WALLET_SECRET, using random keypair',
			err
		);
		return new Keypair();
	}
};
const E2E_WALLET_KEYPAIR = getE2eWalletKeypair();
const E2E_WALLET = new E2eWalletAdapter({ keypair: E2E_WALLET_KEYPAIR });

const { oracleInfos } = getMarketsAndOraclesForSubscription(sdkConfig.ENV);

const pollingFrequencyMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_POLLING_FREQUENCY_MS,
	1000
);

const selectedMarketPollingCadenceMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_SELECTED_MARKET_POLLING_CADENCE_MS,
	1000
);

const activeMarketPollingCadenceMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_ACTIVE_MARKET_POLLING_CADENCE_MS,
	1000
);

const backgroundMarketPollingCadenceMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_BACKGROUND_MARKET_POLLING_CADENCE_MS,
	60_000
);

const auctionExpirySlotBuffer = getNumericEnvVar(
	process.env.NEXT_PUBLIC_AUCTION_EXPIRY_SLOT_BUFFER,
	100
);

const orderMaxTsBufferMs = getNumericEnvVar(
	process.env.NEXT_PUBLIC_ORDER_MAX_TS_BUFFER_MS,
	7_000
);

const delistingConfig = parseDelistingConfig(
	process.env.NEXT_PUBLIC_DELISTING_CONFIG
);
const nextDelistingTs = getNextDelistingTs(
	delistingConfig,
	getNumericEnvVar(process.env.NEXT_PUBLIC_NEXT_DELISTING_TS, 0)
);

const hideMarketIndexes = parseMarketIndexList(
	process.env.NEXT_PUBLIC_HIDE_MARKET_INDEXES
);

const defaultCommitmentLevel: Commitment = (() => {
	const commitmentLevel = process.env.NEXT_PUBLIC_DEFAULT_COMMITMENT_LEVEL;
	switch (commitmentLevel) {
		case 'processed':
		case 'confirmed':
		case 'finalized':
		case 'recent':
		case 'single':
		case 'singleGossip':
		case 'root':
		case 'max':
			return commitmentLevel;
		default:
			return 'finalized';
	}
})();

const feePayerPublicKeyToUse =
	sdkEnv === 'mainnet-beta'
		? process.env.NEXT_PUBLIC_FEE_PAYER_MAINNET_PUBLIC_KEY
		: process.env.NEXT_PUBLIC_FEE_PAYER_DEVNET_PUBLIC_KEY;

if (!feePayerPublicKeyToUse) {
	throw new Error(
		`NEXT_PUBLIC_FEE_PAYER_${
			sdkEnv === 'mainnet-beta' ? 'MAINNET' : 'DEVNET'
		}_PUBLIC_KEY is not set`
	);
}

const Env: EnvironmentVariables = {
	sdkEnv,
	sdkConfig,
	defaultRpcAddress: rpcToUse.value,
	defaultRpcLabel: rpcToUse.label,
	dataApiUrl: dataApiUrlToUse,
	dataApiWsUrl: dataApiWsUrlToUse,
	dataApiEnvToUse,
	devProxyEnvSegment,
	dlobWsServerToUse,
	eventsServerToUse,
	dlobServerHttpUrl: dlobHttpServerToUse,
	selectedMarketPollingCadenceMs,
	activeMarketPollingCadenceMs,
	backgroundMarketPollingCadenceMs,
	pollingFrequencyMs,
	oracleInfos,
	maxAccountsPerWallet: 8,
	locale: 'en-US',
	defaultWallet: DEFAULT_WALLET,
	E2E_WALLET,
	priceDivergenceWarningThreshold: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PRICE_DIVERGENCE_THRESHOLD,
		1
	),
	minInitialDepositValue: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MIN_INITIAL_DEPOSIT_VALUE,
		10
	),
	// Fallbacks for the fee row before the live schedule is readable. They
	// mirror the entry tier of the perp fee structure (`feeTiers[0]`), the most
	// anyone pays.
	defaultMakerRebateBps: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MAKER_REBATE_BPS,
		0.25
	),
	defaultTakerFeeBps: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DEFAULT_TAKER_FEE_BPS,
		4
	),
	dynamicPriorityFeeMultiplier: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PRIORITY_FEE_MULTIPLIER,
		1
	),
	uiEssentialUpdateIntervalMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_UI_ESSENTIAL_UPDATE_INTERVAL_MS,
		1000
	),
	uiNonEssentialUpdateIntervalMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_UI_NON_ESSENTIAL_UPDATE_INTERVAL_MS,
		3000
	),
	dynamicFeeTargetPercentile: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DYNAMIC_FEE_TARGET_PERCENTILE,
		80
	),
	feeSubscriptionPollingMultiplier: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PRIORITY_FEE_SUBSCRIPTION_POLLING_MULTIPLIER,
		5
	),
	maxAccountsSwapBuffer: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MAX_ACCOUNTS_SWAP_BUFFER,
		3
	),
	feePayerPublicKey: feePayerPublicKeyToUse,
	velocityDialectDappAddress:
		(process.env.NEXT_PUBLIC_DRIFT_DIALECT_DAPP_ADDRESS ?? // Can override the dialect address with this variable
		sdkEnv === 'mainnet-beta')
			? 'Cuvh8Ji4scfzJGekSeADhDQrm67Ne9tFoouyHRA6VkJT' // This is the MAINNET Drift Dapp address
			: '9tNykNRDwnaUQ9f1NDazU113bs6z1SeZLZasf5QVYFiL', // This is the DEVNET Drift Dapp address,
	dialectNotificationsEnabled:
		process.env.NEXT_PUBLIC_DIALECT_NOTIFICATIONS_ENABLED === 'true',
	useSimulatedComputeUnitBudget:
		process.env.NEXT_PUBLIC_USE_SIMULATED_COMPUTE_UNIT_BUDGET === 'true',
	useSimulatedComputeUnitBudgetForFees:
		process.env.NEXT_PUBLIC_USE_SIMULATED_COMPUTE_UNIT_BUDGET_FOR_FEES ===
		'true',
	computeUnitBufferMultiplier: getNumericEnvVar(
		process.env.NEXT_PUBLIC_COMPUTE_UNIT_BUFFER_MULTIPLIER,
		1.2
	),
	minimumPriorityFee: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MINIMUM_PRIORITY_FEE,
		1
	) as PriorityFee,
	txSenderRetryInterval:
		parseInt(process.env.NEXT_PUBLIC_TX_SENDER_RETRY_INTERVAL as string) ||
		undefined,
	initialDataSourceMaxDelay: getNumericEnvVar(
		process.env.NEXT_PUBLIC_INITIAL_DATA_SOURCE_MAX_DELAY,
		5_000
	),
	newDataSourceSubMaxDelay: getNumericEnvVar(
		process.env.NEXT_PUBLIC_NEW_DATA_SOURCE_MAX_DELAY,
		2_000
	),
	upgradeDataSourceDelay: getNumericEnvVar(
		process.env.NEXT_PUBLIC_UPGRADE_DATA_SOURCE_DELAY,
		1 * 60 * 1000
	), // 1 minutes
	placeAndTakeLowerBoundCu: getNumericEnvVar(
		process.env.NEXT_PUBLIC_PLACE_AND_TAKE_LOWER_BOUND_CU,
		400_000
	),
	useCachedBlockhashFetcherInVelocityClient: true,
	showAppKitWallet: process.env.NEXT_PUBLIC_SHOW_APPKIT_WALLET === 'true',
	rpcVersion: undefined,
	defaultAuctionDurationPercentage: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DEFAULT_AUCTION_DURATION_PCT,
		33
	),
	perpsMaxLeverage: getNumericEnvVar(process.env.NEXT_PUBLIC_MAX_LEVERAGE, 20),
	swiftServerUrl: swiftServerToUse,
	// This disables adding the oracle crank ixs to various actions like withdraw, deposit, etc.
	disablePervasiveOracleCrank:
		process.env.NEXT_PUBLIC_DISABLE_PERVASIVE_ORACLE_CRANK === 'true',
	signedMessageOrderSigningBudgetMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_SIGNED_MESSAGE_ORDER_SIGNING_BUDGET_MS,
		USER_SIGNING_MESSAGE_BUFFER_MS
	),
	delegateSignedMessageOrderSigningBudgetMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_DELEGATE_SIGNED_MESSAGE_ORDER_SIGNING_BUDGET_MS,
		DELEGATE_SIGNING_MESSAGE_BUFFER_MS
	),
	marketBasedAuctionStartPriceOffsetMajor: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MARKET_BASED_AUCTION_START_PRICE_OFFSET_MAJOR,
		0
	),
	marketBasedAuctionStartPriceOffsetNonMajor: getNumericEnvVar(
		process.env.NEXT_PUBLIC_MARKET_BASED_AUCTION_START_PRICE_OFFSET_NON_MAJOR,
		-0.1
	),
	enableLstPool: process.env.NEXT_PUBLIC_ENABLE_LST_POOL === 'true',
	enablePrivateBetaPerks:
		process.env.NEXT_PUBLIC_ENABLE_PRIVATE_BETA_PERKS === 'true',
	enableOpenBeta: process.env.NEXT_PUBLIC_ENABLE_OPEN_BETA === 'true',
	oracleCrankStalenessThresholdMs: getNumericEnvVar(
		process.env.NEXT_PUBLIC_ORACLE_CRANK_STALENESS_THRESHOLD_MS,
		4_000
	),
	delistingConfig,
	nextDelistingTs,
	hideMarketIndexes,
	auctionExpirySlotBuffer,
	orderMaxTsBufferMs,
	defaultCommitmentLevel,
	funXyzApiKey: process.env.NEXT_PUBLIC_FUN_XYZ_API_KEY as string,
};

/**
 * Checks if the current UI environment is non-production.
 */
export const isDev = () => {
	return (
		isWindowDefined() && process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production'
	);
};

export const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

// Environment-dependent market indexes
export const JLP_POOL_JLP_MARKET_INDEX = sdkEnv === 'mainnet-beta' ? 33 : 5;
export const JLP_POOL_USDC_MARKET_INDEX = sdkEnv === 'mainnet-beta' ? 34 : 6;

// Re-export market constants for backward compatibility
export {
	VELOCITY_TOKEN_BANK_INDEX,
	DSOL_MARKET_INDEX,
	JLP_MARKET_INDEX,
	PRIORITY_SPOT_MARKETS,
} from 'src/constants/markets';
export default Env;
